import { useMemo, useRef, useState } from 'react';
import { format, isSameYear, parseISO } from 'date-fns';
import type { DateRange } from 'react-day-picker';

import { Button } from 'components/ui/button';
import { Calendar } from 'components/ui/calendar';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from 'components/ui/select';
import { X } from 'lucide-react';

import { useEvent } from 'context/event';

import type { ReportDateRange, ReportDateRangeValue } from './api/fetchReportData';
import {
    buildEventDayPresets,
    findMatchingDayPreset,
    type EventDayPreset,
} from 'pages/event/buildEventDayPresets';

type ReportDateRangePickerProps = {
    value: ReportDateRangeValue;
    onChange: (value: ReportDateRangeValue) => void;
};

const LEGACY_DATE_RANGES = ['today', 'last-7-days', 'last-30-days'] as const;

function toDateRangeValue(value: ReportDateRangeValue): DateRange | undefined {
    if (value.range !== 'custom' || !value.customStart || !value.customEnd) {
        return undefined;
    }

    return {
        from: parseISO(value.customStart),
        to: parseISO(value.customEnd),
    };
}

function formatCustomLabel(start: string, end: string): string {
    const from = parseISO(start);
    const to = parseISO(end);

    if (isSameYear(from, to)) {
        return `${format(from, 'MMM d')} – ${format(to, 'MMM d, yyyy')}`;
    }

    return `${format(from, 'MMM d, yyyy')} – ${format(to, 'MMM d, yyyy')}`;
}

function getDisplayLabel(value: ReportDateRangeValue, dayPresets: EventDayPreset[]): string {
    switch (value.range) {
        case 'custom': {
            const dayPreset = findMatchingDayPreset(value.customStart, value.customEnd, dayPresets);
            if (dayPreset) {
                return dayPreset.label;
            }
            if (value.customStart && value.customEnd) {
                return formatCustomLabel(value.customStart, value.customEnd);
            }
            return 'Custom';
        }
        case 'all-time':
            return 'All Time';
        case 'today':
            return 'Today';
        case 'last-7-days':
            return 'Last 7 Days';
        case 'last-30-days':
            return 'Last 30 Days';
        default: {
            const exhaustive: never = value.range;
            return exhaustive;
        }
    }
}

function getSelectValue(value: ReportDateRangeValue, dayPresets: EventDayPreset[]): string {
    if (value.range === 'custom') {
        const dayPreset = findMatchingDayPreset(value.customStart, value.customEnd, dayPresets);
        if (dayPreset) {
            return dayPreset.value;
        }
    }

    return value.range;
}

function isLegacyDateRange(range: ReportDateRange): range is (typeof LEGACY_DATE_RANGES)[number] {
    return LEGACY_DATE_RANGES.some((legacyRange) => legacyRange === range);
}

export function ReportDateRangePicker({ value, onChange }: ReportDateRangePickerProps) {
    const event = useEvent();
    const dayPresets = useMemo(
        () =>
            buildEventDayPresets({
                startDate: event?.start_date,
                endDate: event?.end_date,
                timezone: event?.time_zone,
            }),
        [event?.start_date, event?.end_date, event?.time_zone],
    );
    const previousRangeRef = useRef<ReportDateRangeValue>(value);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [draftRange, setDraftRange] = useState<DateRange | undefined>(() =>
        toDateRangeValue(value),
    );

    const openCustomDialog = () => {
        previousRangeRef.current = value;
        setDraftRange(toDateRangeValue(value));
        setDialogOpen(true);
    };

    const handleSelectChange = (preset: string) => {
        const dayPreset = dayPresets.find((option) => option.value === preset);
        if (dayPreset) {
            onChange({
                range: 'custom',
                customStart: dayPreset.value,
                customEnd: dayPreset.value,
            });
            setDialogOpen(false);
            return;
        }

        switch (preset) {
            case 'custom':
                openCustomDialog();
                if (value.range !== 'custom') {
                    onChange({ range: 'custom' });
                }
                return;
            case 'all-time':
            case 'today':
            case 'last-7-days':
            case 'last-30-days':
                onChange({ range: preset });
                setDialogOpen(false);
                return;
            default:
                return;
        }
    };

    const handleApplyCustomRange = () => {
        if (!draftRange?.from || !draftRange.to) {
            return;
        }

        onChange({
            range: 'custom',
            customStart: format(draftRange.from, 'yyyy-MM-dd'),
            customEnd: format(draftRange.to, 'yyyy-MM-dd'),
        });
        setDialogOpen(false);
    };

    const handleDialogOpenChange = (open: boolean) => {
        if (!open) {
            if (value.range !== 'custom' || !value.customStart || !value.customEnd) {
                onChange(previousRangeRef.current);
            }
        }

        setDialogOpen(open);
    };

    const canApplyCustom = Boolean(draftRange?.from && draftRange.to);

    return (
        <>
            <Select value={getSelectValue(value, dayPresets)} onValueChange={handleSelectChange}>
                <SelectTrigger>
                    <SelectValue placeholder="All Time">
                        {getDisplayLabel(value, dayPresets)}
                    </SelectValue>
                </SelectTrigger>
                <SelectContent className="reports-light-overlay">
                    <SelectItem value="all-time">All Time</SelectItem>
                    {isLegacyDateRange(value.range) ? (
                        <SelectItem value={value.range}>
                            {getDisplayLabel(value, dayPresets)}
                        </SelectItem>
                    ) : null}
                    {dayPresets.map((presetOption) => (
                        <SelectItem key={presetOption.value} value={presetOption.value}>
                            {presetOption.label}
                        </SelectItem>
                    ))}
                    <SelectItem
                        value="custom"
                        onPointerUp={() => {
                            if (getSelectValue(value, dayPresets) === 'custom') {
                                openCustomDialog();
                            }
                        }}
                    >
                        Custom
                    </SelectItem>
                </SelectContent>
            </Select>

            <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
                <DialogContent className="reports-light-overlay w-[calc(100%-2rem)] max-w-[720px] gap-0 overflow-hidden p-0 sm:rounded-lg [&>button]:hidden">
                    <DialogHeader className="relative z-10 shrink-0 border-b border-border bg-card px-6 py-5 pr-14 text-left">
                        <DialogClose className="absolute right-3 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center appearance-none rounded-md border-0 bg-transparent p-0 opacity-70 shadow-none transition-opacity hover:opacity-100 focus:outline-none focus-visible:outline-none">
                            <X className="h-4 w-4" />
                            <span className="sr-only">Close</span>
                        </DialogClose>
                        <DialogTitle>Select dates</DialogTitle>
                        <DialogDescription>
                            Choose a start and end date for your report.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="relative isolate min-h-[400px] overflow-x-auto px-6 py-6">
                        <Calendar
                            mode="range"
                            numberOfMonths={2}
                            showOutsideDays={false}
                            selected={draftRange}
                            onSelect={setDraftRange}
                            disabled={{ after: new Date() }}
                            defaultMonth={draftRange?.from ?? new Date()}
                            className="mx-auto"
                        />
                    </div>

                    <DialogFooter className="gap-2 border-t border-border px-6 py-4 sm:space-x-0">
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDialogOpenChange(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            disabled={!canApplyCustom}
                            onClick={handleApplyCustomRange}
                        >
                            Apply
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
